import React, { useCallback, useEffect, useState } from 'react';
import { Cpu, AlertCircle, CreditCard, Trash2, Plus, AlertTriangle, RefreshCw } from 'lucide-react';
import { getModelDef } from '@platform/shared-types';
import { api } from '../../services/apiService';
import { toastService } from '../../services/toastService';
import { RubyLoader } from '../RubyLoader';

import { RubyGemMark } from '../RubyGemMark';
import { creditMethodForDevice, unknownReaderMessage, registerMayExist } from './payment-types/model';
import { explainPaymentError } from './payment-types/errors';
// Mirror of APOS_CREDIT_TYPES — only one of these may be active on a terminal.
const CREDIT_TYPES = ['credit_external', 'credit_nmi_cloud', 'credit_nmi_direct', 'credit_dejavoo', 'credit_dejavoo_cloud', 'credit_dejavoo_rtpay'] as const;
// Card methods that bind a POI device (NMI cloud/VP3350 + Dejavoo SPIn USB).
const NMI_DEVICE_METHODS = ['credit_nmi_cloud', 'credit_nmi_direct', 'credit_dejavoo', 'credit_dejavoo_cloud', 'credit_dejavoo_rtpay'];

// Derive the credit method from the device MODEL (single source of truth in
// shared-types) — the device list returns `model` reliably. Fall back to the
// enrolled deviceClass. An unknown type is NULL, never a guessed cloud lane:
// that is what bound USB readers as cloud tenders that could not take a card.
const creditMethodFor = (d: { model?: string; deviceClass?: string }): string | null =>
  creditMethodForDevice(d, d.model ? getModelDef(d.model)?.creditMethod : null);

const isVp3350 = (d: { model?: string; deviceClass?: string }): boolean =>
  (d.model ? getModelDef(d.model)?.deviceClass : undefined) === 'nmi_direct_vp3350'
  || d.deviceClass === 'nmi_direct_vp3350';

interface Device {
  deviceId: string;
  deviceName: string;
  model: string;
  serialNumber?: string;
  status: string;
  isActive: boolean;
  registerId: string | null;
  deviceClass?: string;
  terminalIdNmi?: string;
}

interface Props {
  tenantId: string;
  terminal: { terminalId: string; locationId: string; registerName: string };
  /** Bump to refetch when the pool changes elsewhere. */
  refreshKey?: number;
  /** Notify the parent so any sibling views re-read. */
  onChanged?: () => void;
  /** Open the Register-POI modal (parent-managed). */
  onRegisterClick: () => void;
  /** Registration is gated on the location having NMI credentials saved. */
  canRegister: boolean;
  blockReason?: string;
}

/**
 * Per-terminal card readers — a single unified list. Shows only readers relevant
 * to THIS terminal: unassigned devices at the location plus the one bound here.
 * Devices assigned to OTHER terminals are intentionally hidden. Each reader has:
 *   - a radio toggle (activate / deactivate) — exactly one active at a time;
 *     turning one on auto-releases the previous one and enables the matching NMI
 *     credit method (cloud or VP3350); turning the active one off releases it and
 *     disables the card method for this terminal.
 *   - a deregister (delete) action — removes it from the location pool (blocked
 *     while it's the active reader; deactivate it first).
 * All assign/release goes through the existing `putPaymentTypes` contract.
 */
const TerminalCardReaderCard: React.FC<Props> = ({
  tenantId, terminal, refreshKey, onChanged, onRegisterClick, canRegister, blockReason,
}) => {
  const registerId = terminal.terminalId;
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [devices, setDevices] = useState<Device[]>([]);
  const [assigned, setAssigned] = useState<string[]>([]);
  const [enabled, setEnabled] = useState<string[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [confirmDeregister, setConfirmDeregister] = useState<Device | null>(null);

  const fetchAll = useCallback(async (silent = false) => {
    try {
      if (silent) setRefreshing(true); else setLoading(true);
      setError(null);
      const [pool, pt] = await Promise.all([
        api.posNmi.listEmvDevicesAtLocation(tenantId, terminal.locationId),
        /* Skipped for a terminal that has never been claimed: it has no
           register, so this can only answer `register_not_found`, and a 404 the
           browser logs on every mount reads like a fault when it is not. */
        !registerMayExist(terminal as any)
          ? Promise.resolve(null)
          : api.posNmi.getPaymentTypes(tenantId, registerId).catch(() => null),
      ]);
      setDevices(pool?.devices || []);
      if (pt && Array.isArray(pt.assigned)) {
        setAssigned(pt.assigned);
        setEnabled(Array.isArray(pt.enabled) ? pt.enabled : []);
      }
    } catch (e: any) {
      setError(e?.message || 'Failed to load card readers');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [tenantId, terminal.locationId, registerId]);

  useEffect(() => { fetchAll(); }, [fetchAll, refreshKey]);

  // Only readers relevant to THIS terminal: registered/active AND (unassigned OR
  // already bound here). A device bound to another terminal never shows here.
  const visible = devices.filter(d => d.isActive && (!d.registerId || d.registerId === registerId));
  const activeDeviceId = devices.find(d => d.registerId === registerId)?.deviceId || null;

  const applyPut = async (nextAssigned: string[], nextEnabled: string[], emvDeviceId: string | null) => {
    const nmiAssigned = NMI_DEVICE_METHODS.some(m => nextAssigned.includes(m));
    /* Only the lists this swap changes, plus the reader. Override is never
       re-sent (absent = keep), and the server pushes the till — a browser
       push raced it and dropped fields. */
    await api.posNmi.putPaymentTypes(tenantId, registerId, {
      assigned: nextAssigned,
      enabled: nextEnabled,
      ...(nmiAssigned ? { emvDeviceId: emvDeviceId || undefined } : {}),
    });
    setAssigned(nextAssigned);
    setEnabled(nextEnabled);
  };

  const turnOn = async (d: Device) => {
    if (busy) return;
    const method = creditMethodFor(d);
    if (!method) { toastService.error(unknownReaderMessage(d)); return; }
    try {
      setBusy(d.deviceId);
      const nextAssigned = assigned.filter(m => !(CREDIT_TYPES as readonly string[]).includes(m)).concat(method);
      const nextEnabled = enabled.filter(m => !(CREDIT_TYPES as readonly string[]).includes(m)).concat(method);
      await applyPut(nextAssigned, nextEnabled, d.deviceId);
      toastService.success(`${d.deviceName} is now this terminal's card reader`);
      await fetchAll(true);
      onChanged?.();
    } catch (e: any) {
      toastService.error(explainPaymentError(e, 'Could not activate this reader.'));
    } finally {
      setBusy(null);
    }
  };

  const turnOff = async (d: Device) => {
    if (busy) return;
    try {
      setBusy(d.deviceId);
      const nextAssigned = assigned.filter(m => !NMI_DEVICE_METHODS.includes(m));
      const nextEnabled = enabled.filter(m => !NMI_DEVICE_METHODS.includes(m));
      await applyPut(nextAssigned, nextEnabled, null);
      toastService.success(`${d.deviceName} released — card payments off for this terminal`);
      await fetchAll(true);
      onChanged?.();
    } catch (e: any) {
      toastService.error(explainPaymentError(e, 'Could not release this reader.'));
    } finally {
      setBusy(null);
    }
  };

  const deregister = async (d: Device) => {
    try {
      setBusy(d.deviceId);
      setConfirmDeregister(null);
      await api.posNmi.deregisterEmvDevice(tenantId, terminal.locationId, d.deviceId);
      await fetchAll(true);
      onChanged?.();
      toastService.success('Device deregistered');
    } catch (err: any) {
      if (err?.error === 'DEVICE_IN_USE') {
        toastService.error(err.message || 'Reader is active — turn it off first.');
      } else {
        toastService.error(err?.message || 'Deregister failed');
      }
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-stone-200/60 overflow-hidden">
      <div className="px-5 py-4 border-b border-stone-100 flex items-center gap-3">
        <div className="w-9 h-9 rounded-xl bg-stone-100 flex items-center justify-center shrink-0">
          <CreditCard size={17} className="text-stone-600" />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="text-stone-800 font-semibold text-sm">Card Readers — this terminal</h3>
          <p className="text-stone-500 text-xs mt-0.5">
            Only one reader can be active. Turn one on to make it this terminal's card device; the others stay as backups.
          </p>
        </div>
        <button
          type="button"
          onClick={() => fetchAll(true)}
          disabled={refreshing}
          className="p-1.5 rounded-lg hover:bg-stone-100 text-stone-500 disabled:opacity-50"
          title="Refresh"
        >
          {refreshing ? <RubyGemMark size={14} drawing color="currentColor" /> : <RefreshCw size={14} />}
        </button>
        <button
          type="button"
          onClick={onRegisterClick}
          disabled={!canRegister}
          title={!canRegister ? blockReason : 'Register a new reader'}
          className={`inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg shrink-0 ${
            canRegister ? 'bg-emerald-600 text-white hover:bg-emerald-700' : 'bg-stone-200 text-stone-500 cursor-not-allowed'
          }`}
        >
          <Plus size={14} />
          Register
        </button>
      </div>

      {!canRegister && blockReason && (
        <div className="mx-5 my-3 flex items-start gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200">
          <AlertCircle size={12} className="text-amber-600 shrink-0 mt-0.5" />
          <p className="text-xs text-amber-800">{blockReason}</p>
        </div>
      )}

      {loading ? (
        <RubyLoader variant="section" className="py-10" />
      ) : error ? (
        <div className="px-5 py-8 text-center">
          <AlertCircle size={24} className="mx-auto mb-2 text-red-400" />
          <p className="text-stone-600 text-sm">{error}</p>
          <button onClick={() => fetchAll()} className="mt-2 text-sm text-emerald-600 hover:text-emerald-700 font-medium">Retry</button>
        </div>
      ) : visible.length === 0 ? (
        <div className="px-8 py-8 text-center">
          <Cpu size={22} className="mx-auto mb-2 text-stone-300" />
          <p className="text-sm text-stone-500">No readers available for this terminal.</p>
          <p className="text-xs text-stone-400 mt-0.5">Register a reader to add it, then turn it on.</p>
        </div>
      ) : (
        <div className="divide-y divide-stone-100">
          {visible.map((d) => {
            const isActiveReader = d.deviceId === activeDeviceId;
            const isBusy = busy === d.deviceId;
            const isVp = isVp3350(d);
            return (
              <div key={d.deviceId} className={`px-5 py-4 flex items-center gap-3 ${isActiveReader ? 'bg-emerald-50/40' : ''}`}>
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${isActiveReader ? 'bg-emerald-100' : 'bg-stone-100'}`}>
                  <Cpu size={16} className={isActiveReader ? 'text-emerald-600' : 'text-stone-500'} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-stone-800 font-semibold text-sm truncate">{d.deviceName}</p>
                    {isActiveReader && (
                      <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 border border-emerald-200">Active</span>
                    )}
                    {isVp && (
                      <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-stone-100 text-stone-500 border border-stone-200">VP3350</span>
                    )}
                  </div>
                  <p className="text-stone-500 text-xs mt-0.5 truncate">
                    {d.model}{d.serialNumber ? ` · SN ${d.serialNumber}` : ''}
                    {' · '}{isActiveReader ? 'Active on this terminal' : 'Backup — off'}
                  </p>
                </div>

                {/* Activate / deactivate toggle */}
                <button
                  type="button"
                  role="switch"
                  aria-checked={isActiveReader}
                  disabled={isBusy}
                  onClick={() => (isActiveReader ? turnOff(d) : turnOn(d))}
                  title={isActiveReader ? 'Turn off (release reader)' : 'Turn on (make active reader)'}
                  className={`relative inline-flex h-7 w-12 shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-emerald-400/40 disabled:opacity-50 ${isActiveReader ? 'bg-emerald-500' : 'bg-stone-300'}`}
                >
                  {isBusy ? (
                    <span className="text-white absolute left-1/2 -translate-x-1/2 inline-flex"><RubyGemMark size={12} drawing color="currentColor" /></span>
                  ) : (
                    <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${isActiveReader ? 'translate-x-6' : 'translate-x-1'}`} />
                  )}
                </button>

                {/* Deregister — blocked while active; deactivate first */}
                <button
                  type="button"
                  onClick={() => setConfirmDeregister(d)}
                  disabled={isBusy || isActiveReader}
                  title={isActiveReader ? 'Turn off before deregistering' : 'Deregister (permanent)'}
                  className="p-1.5 rounded-lg hover:bg-red-50 text-red-600 disabled:opacity-40 disabled:text-stone-400 disabled:hover:bg-transparent shrink-0"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            );
          })}
        </div>
      )}

      {confirmDeregister && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" role="dialog" aria-modal="true">
          <div className="bg-stone-900 text-white rounded-2xl shadow-xl max-w-md w-full overflow-hidden border border-stone-700">
            <div className="flex items-start gap-3 p-5 border-b border-stone-800">
              <div className="w-10 h-10 rounded-lg bg-red-500/15 border border-red-500/30 flex items-center justify-center shrink-0">
                <AlertTriangle size={18} className="text-red-400" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-white font-semibold text-base">Deregister Reader?</h3>
                <p className="text-stone-400 text-xs mt-0.5">This action cannot be undone.</p>
              </div>
            </div>
            <div className="p-5 space-y-3">
              <p className="text-sm text-stone-200">
                Continue? This will <span className="font-semibold text-red-300">deregister the reader from NMI</span> and remove it from this location's pool.
              </p>
              <div className="rounded-lg bg-stone-800 border border-stone-700 p-3 space-y-1">
                <p className="text-xs text-stone-400">Reader</p>
                <p className="text-sm font-semibold text-white truncate">{confirmDeregister.deviceName}</p>
                <p className="text-[11px] font-mono text-stone-400 truncate">
                  {confirmDeregister.model}
                  {confirmDeregister.serialNumber && ` · ${confirmDeregister.serialNumber}`}
                </p>
              </div>
            </div>
            <div className="p-4 border-t border-stone-800 bg-stone-950 flex items-center justify-end gap-2">
              <button onClick={() => setConfirmDeregister(null)} className="px-4 py-2 text-sm font-medium text-stone-300 hover:text-white">
                Cancel
              </button>
              <button
                onClick={() => deregister(confirmDeregister)}
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg bg-red-600 text-white hover:bg-red-700"
              >
                <Trash2 size={14} />
                Remove and Deregister
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TerminalCardReaderCard;
